import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

import InteractiveImage from './InteractiveImage'
import { framingStyle, layerImages, selectableMaterials } from './configuratorLayers'
import useProductDataStore from '@/context/ProductDataStore'
import { MousePointerClick, Plus, X } from 'lucide-react'
import { IComponent, IProduct, ICustomComponent } from '@/context/ProductDataStore'

interface DynamicHeadphonesProps {
  viewName: string
  engraving: string
  customColourProduct: IProduct
  customComponents: ICustomComponent[]
  toggleColourPicker: (partName: string, mousePos: { x: number; y: number }, position: string | null) => void
  interactive: boolean
  className?: string
  miniature?: boolean
  navHeight?: number
  /** When given, the parent owns which part is selected (the editor's part strip). */
  selectedPart?: { name: string; position: string | null } | null
  /** Show a tap marker on every part that can change colour (needs render anchors). */
  showMarkers?: boolean
  showHint?: boolean
}

type LayerPart = { part_name: string; index: number; position: string | null; variantId: string; src: string; hitSrc: string; isInteractable: boolean }
const partKey = (name: string, position: string | null) => `${name}|${position ?? ''}`

const DynamicHeadphones: React.FC<DynamicHeadphonesProps> = ({
  viewName,
  engraving,
  customColourProduct,
  customComponents,
  toggleColourPicker,
  interactive,
  className = '',
  miniature = false,
  selectedPart,
  showMarkers = false,
  showHint = true,
}) => {
  const [partsData, setPartsData] = useState<LayerPart[]>([])
  const [partIndexClicked, setPartIndexClicked] = useState<number | null>(null)
  const [partPositionClicked, setPartPositionClicked] = useState<string | null>(null)
  const [partIndexHovered, setPartIndexHovered] = useState<number | null>(null)
  const [hoveredPosition, setHoveredPosition] = useState<string | null>(null)
  // A + marker under the mouse wins over the canvas below it, which still sees whatever part is behind the marker.
  const [markerHoverKey, setMarkerHoverKey] = useState<string | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const { activeOrderItemId } = useParams<{ activeOrderItemId: string }>()

  const isMobile = window.matchMedia('(max-width: 812px)').matches
  const framing = framingStyle(customColourProduct?.configuratorRenders)
  const materialInformation = useProductDataStore(state => state.materialInformation)
  const easing = [0.21, 0.035, 0.0, 1.0]

  const preloadedImages = new Set<string>()

  const preloadImage = (src: string) =>
    new Promise<void>((resolve, reject) => {
      if (preloadedImages.has(src)) {
        resolve()
        return
      }

      const img = new Image()
      img.src = src
      img.onload = () => {
        preloadedImages.add(src)
        resolve()
      }
      img.onerror = reject
    })

  const preloadAllImages = async (parts: { src: string }[]) => {
    const preloadPromises = parts.map(part => preloadImage(part.src))
    await Promise.all(preloadPromises)
  }

  useEffect(() => {
    // console.log('CHECKING Dynamic headphones customComponents', customComponents)

    const renders = customColourProduct?.configuratorRenders
    const partsWithImages: LayerPart[] = customComponents
      .filter((component: ICustomComponent) => {
        // Only include components where display exists and configuratorFrontOn is true
        return component.display?.[0]?.configuratorFrontOn === true;
      })
      .map((component: ICustomComponent) => {
        const zIndex = component.display?.[0]?.['z-index'] ?? 0
        const { src, hitSrc } = layerImages(renders, component.componentVariantId.$oid, component.position, component.chosenMaterial.$oid || '')

        // Only parts with a real choice (two or more colours) can be clicked; a single-colour part just shows.
        const isInteractable = (component.display?.[0]?.configuratorFrontOn ?? false)
          && selectableMaterials(component.materialOptions, materialInformation).length > 1

        return {
          part_name: component.name,
          index: zIndex,
          position: component.position,
          variantId: component.componentVariantId.$oid,
          src,
          hitSrc,
          isInteractable
        }
      })
      // Sort by z-index
      .sort((a, b) => a.index - b.index)

    // console.log('CHECKING Final partsWithImages:', partsWithImages)

    preloadAllImages(partsWithImages)
      .then(() => {
        setPartsData(partsWithImages)
      })
      .catch((error) => {
        console.error('Failed to load images:', error)
        // Log which image failed to load
        console.error('Failed to load image:', error.target?.src || 'Unknown image')
        setPartsData(partsWithImages)
      })
  }, [viewName, customComponents, customColourProduct?.configuratorRenders, materialInformation])

  useEffect(() => {
    if (!interactive) {
      setPartIndexClicked(null)
      setPartIndexHovered(null)
      setPartPositionClicked(null)
    }
  }, [interactive])

  // Layers can share a z-index (ear cushions sit with the outer donuts), so only clickable parts can match.
  const interactablePartAt = (index: number | null, position: string | null) =>
    index === null ? undefined : partsData.find(part => part.isInteractable && part.index === index && part.position === position)
  const getPartNameByIndex = (index: number | null, position: string | null) => interactablePartAt(index, position)?.part_name || null

  // Which part to keep bright: the one under the pointer, else the selected one.
  const hovered = interactablePartAt(partIndexHovered, hoveredPosition)
  const clicked = interactablePartAt(partIndexClicked, partPositionClicked)
  const selectedKey = selectedPart !== undefined
    ? (selectedPart ? partKey(selectedPart.name, selectedPart.position) : null)
    : (clicked ? partKey(clicked.part_name, clicked.position) : null)
  const focusKey = markerHoverKey ?? (hovered ? partKey(hovered.part_name, hovered.position) : selectedKey)
  // In the editor something is always selected, so the dimming stays gentle enough to judge the whole look.
  const dimClass = (part: LayerPart) => !focusKey || partKey(part.part_name, part.position) === focusKey
    ? 'brightness-100' : selectedPart !== undefined ? 'brightness-[0.85]' : 'brightness-75'

  // The part under the mouse (else the selected one) is drawn again with a white glow. Layers only
  // hold the pixels the camera sees of their part, so the copies change nothing but the glow around it.
  const glowPart = focusKey ? partsData.find(part => partKey(part.part_name, part.position) === focusKey) : undefined

  const anchors = customColourProduct?.configuratorRenders?.anchors
  const markers = showMarkers && interactive && anchors
    ? partsData.filter(part => part.isInteractable).flatMap(part => {
      const anchor = anchors[`${part.variantId}|${part.position || ''}`]
      return anchor ? [{ part, anchor }] : []
    })
    : []

  return (
    <div className={`dynamic-headphones relative transform-gpu overflow-hidden z-10 w-full h-full flex items-center justify-center
      ${!miniature && ''} 
      ${viewName === 'top_view' ? '-top-20 md:top-0' : ''} 
      ${className}`}>
      <AnimatePresence mode="wait">
        {viewName === 'front_view' ? (
          <motion.div
            key='front'
            initial={{ opacity: 0, filter: 'blur(5px)', transform: 'scale(1.05)' }}
            animate={{ opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }}
            transition={{ duration: 0.2 }}
            exit={{ opacity: 0, filter: 'blur(10px)', transform: 'scale(0.95)' }}
            className={`relative w-full h-full flex items-center justify-center transform-gpu ${!miniature ? '' : ''}`}>
            <div className="relative w-full h-full max-w-full max-h-full object-contain" style={framing}>
              <AnimatePresence>
                {partsData.map((part, index) => (
                  <motion.img
                    src={part.src}
                    key={index}
                    initial={{ y: '-10%', opacity: 0 }}
                    animate={{ y: '0%', opacity: 1, transition: { y: { duration: 2.4, ease: [0, 1, 0, 1] }, opacity: { duration: 0.8, ease: [0, 0, 0, 1] } } }}
                    exit={{ y: '-2%', opacity: 0 }}
                    style={{ zIndex: part.index }}
                    className={`absolute w-full h-full object-contain transform-gpu transition-none top-0 left-0 ${dimClass(part)}`}
                  />
                ))}
              </AnimatePresence>
              {glowPart && (
                <>
                  {/* Soft halo behind every layer: it only shows against the background, never over other parts. */}
                  <img
                    key={`halo-${partKey(glowPart.part_name, glowPart.position)}`}
                    src={glowPart.src}
                    alt=""
                    aria-hidden="true"
                    style={{ zIndex: -1 }}
                    className="pointer-events-none absolute left-0 top-0 h-full w-full object-contain animate-part-glow-in motion-reduce:animate-none
                      [filter:drop-shadow(0_0_3px_#fff)_drop-shadow(0_0_10px_rgba(255,255,255,0.9))]"
                  />
                  {/* Crisp white edge on top, so the outline also shows where the part meets its neighbours. */}
                  <img
                    key={`edge-${partKey(glowPart.part_name, glowPart.position)}`}
                    src={glowPart.src}
                    alt=""
                    aria-hidden="true"
                    style={{ zIndex: 50 }}
                    className="pointer-events-none absolute left-0 top-0 h-full w-full object-contain animate-part-glow-in motion-reduce:animate-none
                      [filter:drop-shadow(0_0_1px_#fff)_drop-shadow(0_0_1.5px_#fff)]"
                  />
                </>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key='top'
            initial={{ opacity: 0, filter: 'blur(5px)', transform: 'scale(1.05)' }}
            animate={{ opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }}
            transition={{ duration: 0.2 }}
            exit={{ opacity: 0, filter: 'blur(10px)', transform: 'scale(0.95)' }}
            className={`relative transform-gpu w-full h-full flex items-center justify-center md:top-0`}>
            <div className="relative w-full h-full max-w-full max-h-full object-contain" style={framing}>
              {partsData.map(part => (
                <img
                  src={part.src}
                  key={part.index}
                  style={{ zIndex: part.index }}
                  className={`${part.index > 0 ? 'absolute' : 'relative'} w-full h-full object-contain ${partIndexHovered === null ? 'brightness-100' : (partIndexHovered === part.index ? 'brightness-100' : 'brightness-75')} transition-all duration-100 ease-kibu top-0 left-0`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {viewName === 'front_view' && interactive && showHint && (
        <motion.div
          key='top'
          initial={{ opacity: 0, filter: 'blur(0px)', transform: 'scale(1.3)' }}
          animate={{ opacity: 0.7, filter: 'blur(0px)', transform: 'scale(1)' }}
          transition={{ duration: 0.6, ease: easing }}
          exit={{ opacity: 0, filter: 'blur(10px)', transform: 'scale(0.9)' }}
          className={`absolute transform-gpu flex justify-center text-xs md:text-base items-center w-94 h-32 ${!miniature && '-top-14'} md:-top-20 left-0 right-0 bottom-0 m-auto`}>
          {partIndexClicked ? (
            <div className='flex flex-col justify-center items-center gap-1 mb-20'>
              <X className='h-6 w-6' />
              Click anywhere to deselect
            </div>
          ) : (
            <div className='animate-pulse flex flex-col justify-center items-center gap-1 mb-20'>
              <MousePointerClick className='w-6 h-6' />
              Click Part to Choose Colour
            </div>
          )}
        </motion.div>
      )}
      {viewName === 'top_view' && engraving !== '' && (
        <motion.div
          key='top'
          initial={{ opacity: 0, filter: 'blur(0px)', transform: 'scale(1.3)' }}
          animate={{ opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }}
          transition={{ duration: 0.6, ease: easing }}
          exit={{ opacity: 0, filter: 'blur(10px)', transform: 'scale(0.9)' }}
          className={`absolute transform-gpu flex justify-center items-center w-94 h-32 top-2 md:top-0 left-0 right-0 bottom-0.5 m-auto text-[4cqw] md:text-[2.5cqw]`}
          style={{
            backgroundColor: '#aaaaaa',
            color: 'transparent',
            textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
            backgroundClip: 'text',
            mixBlendMode: 'multiply'
          }}>
          {engraving}
        </motion.div>
      )}
      {partsData && interactive && (
        <InteractiveImage
          parts={partsData}
          setPartIndexHovered={(index: number | null, position: string | null) => {
            setPartIndexHovered(index)
            setHoveredPosition(position)
          }}
          setPartIndexClicked={(index: number | null, position: string | null) => {
            setPartIndexClicked(index)
            setPartPositionClicked(position)
            setHoveredPosition(null)
          }}
          setMousePosition={setMousePosition}
          partClicked={(index: number | null, position: string | null, mousePos: { x: number, y: number }) =>
            toggleColourPicker(getPartNameByIndex(index, position) || '', mousePos, position)}
          style={framing}
          // Must sit exactly over the images: any offset here moves the tap zones off the parts.
          className="absolute inset-0 w-full h-full"
        />
      )}
      {markers.length > 0 && (
        // Same box and zoom as the images; the inner square is the images' object-contain area.
        <div className="pointer-events-none absolute inset-0 z-20 [container-type:size]" style={framing}>
          <div className="absolute left-1/2 top-1/2 h-[min(100cqw,100cqh)] w-[min(100cqw,100cqh)] -translate-x-1/2 -translate-y-1/2">
            {markers.map(({ part, anchor }) => {
              const selected = selectedKey === partKey(part.part_name, part.position)
              return (
                <button
                  key={partKey(part.part_name, part.position)}
                  type="button"
                  aria-label={`Change the ${part.position ? `${part.position} ` : ''}${part.part_name.toLowerCase()} colour`}
                  aria-pressed={selected}
                  onClick={event => toggleColourPicker(part.part_name, { x: event.clientX, y: event.clientY }, part.position)}
                  // Mouse only: taps on a touch screen also send pointer enter, which would leave the highlight stuck.
                  onPointerEnter={event => { if (event.pointerType === 'mouse') setMarkerHoverKey(partKey(part.part_name, part.position)) }}
                  onPointerLeave={() => setMarkerHoverKey(null)}
                  style={{ left: `${anchor[0] * 100}%`, top: `${anchor[1] * 100}%` }}
                  className={`colour-picker-button pointer-events-auto absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-md ring-1 transition-colors duration-200
                    ${selected ? 'bg-black text-white ring-white' : 'bg-white/90 text-black ring-black/10 hover:bg-white'}`}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default DynamicHeadphones
